import React, { useState, useEffect, useCallback } from 'react';
import { ListPlus, Wand2, PenLine, Gauge, LayoutPanelLeft } from 'lucide-react';
import { useDatabaseSafe } from '../../context/DatabaseContext';
import { useNotifications } from '../../context/NotificationContext';
import { dsSpecApi } from '../../services/api';
import IndexSheetBuilderTab from './IndexSheetBuilderTab';
import SpecBrowser from './SpecBrowser';
import PanelSpecTab from './PanelSpecTab';
import { prefillFromCatalog, errText } from './deviceSpec';

// Device Specification Utility: build Index Sheets (three Builder tabs sharing one form state) and
// pick device and panel specifications for this database. The route is gated on utilities.ds.project.composer; every tab uses that key.
// Pulling a catalog sheet in also needs utilities.ds.project.catalog on this database.
const BUILDER_TABS = ['guided', 'suggested', 'manual'];
const TABS = [
  { id: 'guided',    label: 'Guided Builder',     icon: ListPlus },
  { id: 'suggested', label: 'Suggested Grouping', icon: Wand2 },
  { id: 'manual',    label: 'Manual Builder',     icon: PenLine },
  { id: 'device',    label: 'Device Spec',        icon: Gauge },
  { id: 'panel',     label: 'Panel Spec',         icon: LayoutPanelLeft },
];

const DeviceSpecificationUtilityPage = () => {
  const { notify } = useNotifications();
  const dbSlug = useDatabaseSafe()?.dbSlug || '';
  const [activeTab, setActiveTab] = useState(TABS[0].id);
  // This database's ISA types, manufacturers and valve sizes: the Builder's pickers, and what "Open
  // in Builder" matches a catalog code against.
  const [ref, setRef] = useState({ isaTypes: [], manufacturers: [], sizes: [] });
  const [prefill, setPrefill] = useState(null);

  const loadRef = useCallback(async () => {
    try {
      const [i, m, z] = await Promise.all([dsSpecApi.getIsaTypes(), dsSpecApi.getManufacturers(), dsSpecApi.getSizes()]);
      setRef({ isaTypes: i.data ?? [], manufacturers: m.data ?? [], sizes: z.data ?? [] });
    } catch (e) { notify.error(errText(e, 'Failed to load this database\'s ISA types and manufacturers.')); }
  }, [notify]);

  useEffect(() => { loadRef(); }, [loadRef, dbSlug]);

  const openInBuilder = row => {
    const p = prefillFromCatalog(row, ref.isaTypes, ref.manufacturers);
    if (!p) { notify.warning(`${row.indexSheet} does not follow the code grammar.`); return; }
    setPrefill({ ...p, nonce: Date.now() });
    setActiveTab('guided');
  };

  return (
    <div className="stui-module-container">
      <div className="stui-module-header">
        <div className="stui-module-header-left">
          <h1>Device Specification Utility</h1>
          <div className="stui-module-header-tabs">
            {TABS.map(({ id, label, icon: Icon }) => (
              <button key={id} className={`stui-dataentry-tab${activeTab === id ? ' active' : ''}`}
                onClick={() => setActiveTab(id)}>
                <Icon size={14} /><span>{label}</span>
              </button>
            ))}
          </div>
        </div>
      </div>
      {BUILDER_TABS.includes(activeTab) && (
        <IndexSheetBuilderTab mode={activeTab} onModeChange={setActiveTab} isaTypes={ref.isaTypes} manufacturers={ref.manufacturers} sizes={ref.sizes}
          prefill={prefill} onPrefillUsed={() => setPrefill(null)} onCreated={loadRef} />
      )}
      {activeTab === 'device' && (
        <div className="stui-tab-body">
          <SpecBrowser type="Tag" dbSlug={dbSlug} onOpenInBuilder={openInBuilder} />
        </div>
      )}
      {activeTab === 'panel' && <PanelSpecTab dbSlug={dbSlug} onOpenInBuilder={openInBuilder} />}
    </div>
  );
};

export default DeviceSpecificationUtilityPage;
